import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function TemplateFormDialog({ open, onOpenChange, onSaved, template }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [autoCreateTasks, setAutoCreateTasks] = useState(false);
  const [items, setItems] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!template;

  useEffect(() => {
    if (open) {
      setName(template?.name || "");
      setCategory(template?.category || "");
      setDescription(template?.description || "");
      setAutoCreateTasks(template?.auto_create_tasks || false);
      setItems(template?.items?.length ? template.items.map((i) => i.text) : [""]);
    }
  }, [open, template]);

  const updateItem = (idx, value) => setItems((prev) => prev.map((it, i) => (i === idx ? value : it)));
  const addItemRow = () => setItems((prev) => [...prev, ""]);
  const removeItemRow = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    const cleanItems = items.map((t) => t.trim()).filter(Boolean);
    if (!name.trim() || !category.trim() || cleanItems.length === 0) {
      toast.error("Name, category, and at least one checklist item are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { name: name.trim(), category: category.trim(), description: description.trim() || null, auto_create_tasks: autoCreateTasks, items: cleanItems.map((t) => ({ text: t })) };
      if (isEdit) {
        await api.put(`/templates/${template.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post("/templates", payload);
        toast.success("Template created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="template-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Checklist Template" : "New Checklist Template"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. ESG Onboarding Checklist" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Input data-testid="template-category-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. ESG, GHG, Custom..." />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea data-testid="template-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="flex items-center justify-between border border-[#E2E8F0] rounded-md px-3 py-2.5">
            <div>
              <p className="text-sm font-medium text-[#1C1C1E]">Auto-create Tasks</p>
              <p className="text-xs text-[#4B5563]">Each item creates a linked Task Board entry when assigned</p>
            </div>
            <Switch data-testid="template-auto-tasks-switch" checked={autoCreateTasks} onCheckedChange={setAutoCreateTasks} />
          </div>
          <div className="space-y-2">
            <Label>Checklist Items</Label>
            {items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Input data-testid={`template-item-input-${idx}`} value={it} onChange={(e) => updateItem(idx, e.target.value)} placeholder={`Item ${idx + 1}`} />
                {items.length > 1 && (
                  <button data-testid={`template-remove-item-${idx}`} onClick={() => removeItemRow(idx)} className="text-[#4B5563] hover:text-red-600"><X className="h-4 w-4" /></button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addItemRow} data-testid="template-add-item-button">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="template-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="template-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
